import React, { memo } from 'react';

import cn from 'classnames';

import { AngleDown, AngleRight } from 'styles/assets/icons';
import Box from 'UIKit/Box';
import CollapsibleCard from 'UIKit/CollapsibleCard';
import DeltaButton from 'UIKit/DeltaButton/DeltaButton';
import ToggleExpandCollapseIcon from 'UIKit/ToggleExpandCollapseIcon';

import styles from './faqItem.module.scss';

// #region icons

const ExpandedIcon = () => {
  return (
    <Box className={cn(styles.faqIcon)}>
      <AngleDown color="var(--brand-bg-primary)" />
    </Box>
  );
};

const CollapseIcon = () => {
  return (
    <Box className={cn(styles.faqIcon)}>
      <AngleRight color="var(--main-text-secondary)" />
    </Box>
  );
};

// #endregion

interface FaqItemProps {
  question: React.ReactNode;
  answer: React.ReactNode;
  id: React.Key;
  expandIcon?: React.ReactNode;
  collapseIcon?: React.ReactNode;
  classes?: {
    button?: string;
    question?: string;
    answer?: string;
    root?: string;
  };
}

/**
 * @description Reusable components for FAQ item .
 *
 */
const FaqItem = ({
  question,
  answer,
  expandIcon,
  id,
  collapseIcon,
  classes,
}: FaqItemProps) => {
  return (
    <Box className={cn(styles.faqItemContainer, classes?.root)}>
      <CollapsibleCard expandedHeight="fit-content">
        {(toggleCollapsed, isCollapsed) => {
          return (
            <>
              <DeltaButton
                onClick={toggleCollapsed}
                data-testid={`faqToggle-${id}`}
                className={cn(
                  styles.faqBtn,
                  {
                    [styles.faqBackground1]: isCollapsed,
                    [styles.faqBackground2]: !isCollapsed,
                  },
                  classes?.button
                )}
                fullWidth
                useBasicButton
              >
                <Box className={cn(styles.faqQues, classes?.question)}>
                  <Box as="span">{question}</Box>{' '}
                  <ToggleExpandCollapseIcon
                    isExpanded={!isCollapsed}
                    customExpandIcon={expandIcon || <ExpandedIcon />}
                    customCollapseIcon={collapseIcon || <CollapseIcon />}
                    testID="collapse-contract-type-filter"
                  />
                </Box>
              </DeltaButton>

              <Box
                renderWhen={!isCollapsed}
                className={cn(styles.faqAns, classes?.answer)}
                data-testid={`FAQ-answer-${id}`}
              >
                {answer}
              </Box>
            </>
          );
        }}
      </CollapsibleCard>
    </Box>
  );
};

export default memo(FaqItem);
